import React, { useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { fetchDoctorOPDAssessment } from "../../../store/slices/doctorOPDAssessmentsSlice";
import PrintWrapper, { printFooterStyles } from "../../common/PrintWrapper";
import logo from "../../../assets/images/logo-samplee.png";

export default function PrescriptionSlipPrint() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { currentDoctorOPDAssessment, isLoading, error } = useSelector((state) => state.doctorOPDAssessments);

  useEffect(() => {
    if (id) dispatch(fetchDoctorOPDAssessment(id));
  }, [dispatch, id]);

  useEffect(() => {
    if (currentDoctorOPDAssessment) {
      const timer = setTimeout(() => window.print(), 800);
      window.onafterprint = () => navigate(-1);
      return () => {
        clearTimeout(timer);
        window.onafterprint = null;
      };
    }
  }, [currentDoctorOPDAssessment, navigate]);

  if (isLoading) return <div className="p-8 text-center">Loading Prescription Slip...</div>;
  if (error) return <div className="p-8 text-danger text-center">{error}</div>;
  if (!currentDoctorOPDAssessment) return <div className="p-8 text-center">No record found.</div>;

  const a = currentDoctorOPDAssessment;
  const p = a.patient || {};
  const labs = a.labs || [];

  const fmtDate = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
      : "—";

  const calculateAge = (dob) => {
    if (!dob) return "—";
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  return (
    <>
      <style jsx global>{`
        * {
          box-sizing: border-box !important;
          font-family: Arial, sans-serif !important;
        }

        body {
          background: #fff !important;
          color: #000 !important;
          font-size: 11px !important;
          margin: 0 !important;
          padding: 0 !important;
        }

        .print-container {
          padding: 30px !important;
          position: relative !important;
        }
        ${printFooterStyles}

        .logo {
          position: absolute !important;
          top: 15px !important;
          left: 15px !important;
        }

        .logo img {
          height: 60px !important;
        }

        .header {
          text-align: center !important;
          margin-bottom: 20px !important;
          border-bottom: 2px solid #000 !important;
          padding-bottom: 10px !important;
        }

        .header h3 {
          font-size: 1.5rem !important;
          font-weight: bold !important;
        }

        .header h6 {
          font-size: 1rem !important;
          text-decoration: underline !important;
        }

        .section-title {
          background: #eee !important;
          padding: 4px 8px !important;
          font-weight: bold !important;
          margin: 15px 0 8px 0 !important;
          border: 1px solid #000 !important;
          text-transform: uppercase !important;
        }

        .info-table {
          width: 100% !important;
          border-collapse: collapse !important;
          margin-bottom: 10px !important;
        }

        .info-table td {
          border: 1px solid #000 !important;
          padding: 6px !important;
          vertical-align: top !important;
        }

        .label {
          font-weight: bold !important;
          width: 25% !important;
          background: #f9f9f9 !important;
        }

        .text-box {
          border: 1px solid #000 !important;
          padding: 8px !important;
          min-height: 50px !important;
          margin-top: 5px !important;
          white-space: pre-wrap !important;
        }
      `}</style>

      <PrintWrapper>
        <div className="print-container">
          <div className="logo"><img src={logo} alt="TRH Logo" /></div>
          <div className="header">
            <h3>THE RECOVERY HOUSE</h3>
            <h6>Prescription Slip</h6>
          </div>

          {/* PATIENT INFO */}
          <div className="section-title">Patient Information</div>
          <table className="info-table">
            <tbody>
              <tr>
                <td className="label">Patient Name</td>
                <td>{p.first_name} {p.last_name}</td>
                <td className="label">MR No.</td>
                <td>{p.mr_no || "—"}</td>
              </tr>
              <tr>
                <td className="label">Age</td>
                <td>{calculateAge(p.dob)}</td>
                <td className="label">Gender</td>
                <td>{p.gender || "—"}</td>
              </tr>
            </tbody>
          </table>

          {/* SECTION 1: LABS & DIAGNOSIS */}
          <div className="section-title">Labs & Diagnosis</div>
          <table className="info-table">
            <thead>
              <tr>
                <th style={{ width: '10%', border: '1px solid #000', padding: '6px', textAlign: 'left', background: '#f9f9f9' }}>#</th>
                <th style={{ border: '1px solid #000', padding: '6px', textAlign: 'left', background: '#f9f9f9' }}>Lab Test Details</th>
              </tr>
            </thead>
            <tbody>
              {labs.length > 0 ? labs.map((lab, i) => (
                <tr key={i}>
                  <td style={{ border: '1px solid #000', padding: '6px' }}>{i + 1}</td>
                  <td style={{ border: '1px solid #000', padding: '6px' }}>{lab.test_name}</td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="2" style={{ border: '1px solid #000', padding: '6px', textAlign: 'center' }}>No labs listed</td>
                </tr>
              )}
            </tbody>
          </table>
          <table className="info-table" style={{ marginTop: '5px' }}>
            <tbody>
              <tr>
                <td className="label">Provisional Diagnosis</td>
                <td>{a.provisional_diagnosis || "—"}</td>
              </tr>
              <tr>
                <td className="label">Actual Diagnosis</td>
                <td>{a.actual_diagnosis || "—"}</td>
              </tr>
            </tbody>
          </table>

          {/* SECTION 2: TREATMENT PLAN */}
          <div className="section-title">Treatment Plan</div>
          <div className="label" style={{ padding: '4px 8px', border: '1px solid #000', borderBottom: 'none' }}>Pharmacotherapy</div>
          <div className="text-box">{a.pharmacotherapy || "—"}</div>

          <div className="label" style={{ padding: '4px 8px', border: '1px solid #000', borderBottom: 'none', marginTop: '10px' }}>Special Orders</div>
          <div className="text-box">{a.special_order || "—"}</div>

          {/* SECTION 3: CONSULTANT REVIEW */}
          <div className="section-title">Consultant Review & Signatures</div>
          <div className="text-box" style={{ minHeight: '80px' }}>
            <strong>Consultant Note:</strong><br />
            {a.consultant_review || "—"}
          </div>
          <table className="info-table" style={{ marginTop: '10px' }}>
            <tbody>
              <tr>
                <td className="label">Consultant Name</td>
                <td>{a.consultant_name || "—"}</td>
                <td className="label">Date</td>
                <td>{fmtDate(a.consultant_date)}</td>
              </tr>
              <tr>
                <td className="label">Signature / Stamp</td>
                <td>{a.consultant_signature || " "}</td>
                <td className="label">Time</td>
                <td>{a.consultant_time || "—"}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </PrintWrapper>
    </>
  );
}
